import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GlobeIcon, SmartphoneIcon, ZapIcon, TargetIcon, SearchIcon, ArrowRightIcon, CheckCircleIcon, CodeIcon, ChevronDownIcon, BuildingIcon, StarIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { CodeTypingEffect } from '../components/CodeTypingEffect';
import { SEOHead } from '../components/SEOHead';
export function WebDesignSeoPage() {
  const faqs = [{
    question: 'Do I need a new website to work with you?',
    answer: 'Not necessarily. If your current website is fast, mobile-friendly, and converts well, we can often just implement our AI lead conversion tools and ad management. However, if your site is slow or outdated, a new conversion-focused site will significantly improve your ad ROI.'
  }, {
    question: 'What makes your websites different from a standard template?',
    answer: 'We build websites specifically as conversion assets for home service businesses. They are designed to load instantly, look perfect on mobile, and guide visitors directly toward booking an estimate or making a call.'
  }, {
    question: 'Do you provide ongoing SEO services?',
    answer: 'Yes, but we focus on local SEO and GEO (Generative Engine Optimization) as a support layer for discoverability. We ensure your site is structured correctly, loads fast, and has crawlable service content so local customers and AI search engines can find you.'
  }, {
    question: 'How long does it take to build a new website?',
    answer: 'A standard conversion website typically takes 2-4 weeks from kickoff to launch, depending on how quickly we receive your branding assets and approvals.'
  }, {
    question: 'Are your websites mobile-friendly?',
    answer: 'Yes. Over 70% of local service searches happen on mobile devices. We design mobile-first, ensuring that click-to-call buttons and lead forms are immediately accessible on small screens.'
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-2938" data-mp="o3oif110crf4">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-2939" data-mp="o5hsyreqrvse">
          <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-2940" data-mp="1gscb3ho36j2p">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-2941" data-mp="1720yl6qc71k7" />
        </button>
        <AnimatePresence data-id="element-2942" data-mp="1zqoj3039hmwp">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-2943" data-mp="1zp3rnjc8uk32">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-2944" data-mp="2pznlpbzvtzbk">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="bg-viridia-black min-h-screen" data-id="element-2945" data-mp="2zqqqoovxmyg4">
      <SEOHead title="Web Design & Local SEO | Conversion Websites | Viridia Analytics" description="We build fast, mobile-first websites designed to convert local traffic into booked jobs. Supported by local SEO and GEO for discoverability." canonical="/web-design-seo" keywords="web design Phoenix, local SEO Phoenix, marketing agency phoenix, contractor websites, HVAC websites, painter websites, landscaper websites, window installer websites, mobile-first website, conversion-focused design, local search optimization, website development Phoenix" faq={faqs} data-id="element-2946" data-mp="2zr0ec5ti8tin" />
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-2947" data-mp="34u9hfdt9t52l">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-2948" data-mp="2h45dt6g315bl">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-viridia/10 blur-[120px] animate-pulse-slow" data-id="element-2949" data-mp="2d3v6nqgk08v5"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-2950" data-mp="2r0rurk5afc8c">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-2951" data-mp="2m2glpbmth5r6">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-2952" data-mp="6eghid5fbvm1">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-2953" data-mp="1chfp7pc16r7">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-semibold tracking-wide mb-6" data-id="element-2954" data-mp="1ac26yuoasy1q">
                  WEB DESIGN & SEO
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-2955" data-mp="270qjxcl5pv4a">
                  Turn Searches Into{' '}
                  <span className="text-viridia" data-id="element-2956" data-mp="1t8k4xttqpu6f">Customers</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2957" data-mp="1y7elep48j6nd">
                  Your website should not just be a digital brochure. We build
                  fast, mobile-first websites that act as conversion
                  infrastructure, supported by local SEO to ensure you are found
                  when customers are searching.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-2958" data-mp="2vlwcqi1tkyzw">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-2959" data-mp="2vjjvy87ssuof">
                    Book a Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-2960" data-mp="2cvey1hdto3xj">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration - Browser Mockup */}
            <div className="relative hidden lg:block" data-id="element-2961" data-mp="1tx2mxq3cojzb">
              <motion.div initial={{
              opacity: 0,
              y: 40,
              rotateX: 10
            }} animate={{
              opacity: 1,
              y: 0,
              rotateX: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} style={{
              perspective: '1000px'
            }} className="w-full" data-id="element-2962" data-mp="9erwdcfizrnl">
                <div className="bg-viridia-surface border border-gray-700 rounded-xl overflow-hidden shadow-2xl transform rotate-y-[-5deg] rotate-z-[2deg]" data-id="element-2963" data-mp="3qv9lu7ke8dao">
                  {/* Browser Chrome */}
                  <div className="bg-gray-900 px-4 py-3 flex items-center border-b border-gray-800" data-id="element-2964" data-mp="4gq9egezb86u">
                    <div className="flex space-x-2" data-id="element-2965" data-mp="334vtsh1t6eg3">
                      <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-2966" data-mp="383p6w8d6o3fq"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-2967" data-mp="21gwn5shzy166"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-2968" data-mp="21j0pw745su4v"></div>
                    </div>
                    <div className="mx-auto bg-gray-800 text-gray-400 text-xs px-4 py-1 rounded-md flex items-center" data-id="element-2969" data-mp="1wjwdl7lxugab">
                      <SearchIcon className="w-3 h-3 mr-2" data-id="element-2970" data-mp="25irmirgda4hb" />
                      phoenix-local-services.com
                    </div>
                  </div>
                  {/* Real-time Coding Effect */}
                  <CodeTypingEffect data-id="element-2971" data-mp="2fgxpzdhtnioa" />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD SECTION */}
      <section className="py-24 bg-viridia-light" data-id="element-2972" data-mp="2p6h64be5gyfg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2973" data-mp="2p7s8c6ry5y4g">
          <ScrollReveal data-id="element-2974" data-mp="335qmbb2cvsjf">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2975" data-mp="337beiu176ze4">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2976" data-mp="2y7fdn3nsro2s">
                Websites Built to Convert
              </h2>
              <p className="text-lg text-gray-600" data-id="element-2977" data-mp="3htiade6gjw2k">
                Not just pretty - built for speed, mobile-first, and optimized
                to turn visitors into paying customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2978" data-mp="3ctw09dlxevxz">
            <ScrollReveal delay={0.1} data-id="element-2979" data-mp="3cs1czsn6mrqq">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2980" data-mp="2z2r44lhrolse">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2981" data-mp="33uqzs1qt0sj6">
                  <ZapIcon className="w-7 h-7" data-id="element-2982" data-mp="2p270xdaot77j" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2983" data-mp="2u464ooaca650">
                  Speed-Optimized
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2984" data-mp="2g5pxkw8kedmc">
                  Lightning-fast load times that keep visitors engaged and
                  improve your Google ranking. No bloated templates.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-2985" data-mp="bh0f7v1zw9s3">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2986" data-mp="bdusunkr3m02">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2987" data-mp="1j2u3gffmt3p">
                  <SmartphoneIcon className="w-7 h-7" data-id="element-2988" data-mp="p85j59cebo88" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2989" data-mp="u5nj4a8fqt43">
                  Mobile-First Design
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2990" data-mp="gf1zgjb5qf55">
                  Over 70% of your customers browse on their phone. Every site
                  we build looks and works perfectly on mobile.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-2991" data-mp="1cy88hhoxeg35">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2992" data-mp="1czb0xu1wcql6">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2993" data-mp="2spai8e93ckro">
                  <TargetIcon className="w-7 h-7" data-id="element-2994" data-mp="37c3oyv4joi89" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2995" data-mp="32dsgootwefs5">
                  Conversion-Focused
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2996" data-mp="2epixzvrze858">
                  Strategic layouts, clear CTAs, and trust signals designed to
                  turn visitors into leads and booked jobs.
                </p>
              </div>
            </ScrollReveal>
          </div>

          {/* MOBILE-FIRST SHOWCASE */}
          <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-2997" data-mp="2jmj6edcazmzi">
            <ScrollReveal data-id="element-2998" data-mp="3uvb0mcxu2it">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-2999" data-mp="3v2zn09zlg9ny">
                Mobile-First
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-3000" data-mp="az3co2v9objz">
                Designed for the screen in their hand
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-3001" data-mp="fx4z8xrwit07">
                Over 70% of local service searches happen on a phone. We build
                every site mobile-first, so the moment a homeowner lands on your
                page they can tap to call, request a quote, or book - no
                pinching, zooming, or hunting.
              </p>
              <ul className="space-y-3" data-id="element-3002" data-mp="3tcb2lxd3tbju">
                {['Tap-to-call and click-to-text buttons within thumb reach', 'Fast-loading pages even on cellular data', 'Lead forms that are effortless to fill out on mobile'].map((item, i) => <li key={i} className="flex items-start" data-id="element-3003" data-mp="35m6wnjcg0zpo" data-mpi={i}>
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-3004" data-mp="1ms5fkp4t4iqw" />
                    <span className="text-gray-700" data-id="element-3005" data-mp="1ismvfvkt8bp4">{item}</span>
                  </li>)}
              </ul>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-3006" data-mp="1dublzlyfg37f">
              <div className="flex justify-center" data-id="element-3007" data-mp="18u72dn282scz">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.7
              }} className="relative" data-id="element-3008" data-mp="18viafz2pam9w">
                  <motion.div animate={{
                  y: [0, -12, 0]
                }} transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} aria-label="Mobile-first website preview on a smartphone" role="img" className="relative w-[280px] h-[580px] rounded-[3rem] bg-gray-900 p-3 shadow-2xl border border-gray-800" data-id="element-3009" data-mp="l3bidc40d1ga">
                    {/* Notch */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl z-20" data-id="element-3010" data-mp="1xfp20ldsibjy"></div>
                    {/* Screen */}
                    <div className="relative w-full h-full rounded-[2.3rem] overflow-hidden bg-white" data-id="element-3011" data-mp="1nikbk9cwvtxc">
                      {/* Site nav */}
                      <div className="flex items-center justify-between px-4 pt-6 pb-3 bg-viridia-black" data-id="element-3012" data-mp="1si6r4pg7pm9b">
                        <span className="text-white font-bold text-sm" data-id="element-3013" data-mp="3hwv0sixf4q3n">
                          Summit HVAC
                        </span>
                        <div className="flex flex-col gap-1" data-id="element-3014" data-mp="1b82v3qqjslya">
                          <span className="block w-5 h-0.5 bg-white" data-id="element-3015" data-mp="1b8cl3eblet3z"></span>
                          <span className="block w-5 h-0.5 bg-white" data-id="element-3016" data-mp="16a1dzbcomwpg"></span>
                          <span className="block w-5 h-0.5 bg-white" data-id="element-3017" data-mp="1kyeaqajlgjl0"></span>
                        </div>
                      </div>
                      {/* Hero */}
                      <div className="bg-viridia-black px-4 pb-6 pt-2" data-id="element-3018" data-mp="1g5crjqcctxvm">
                        <h4 className="text-white font-extrabold text-lg leading-tight mb-2" data-id="element-3019" data-mp="1twzolse4fafg">
                          Fast, Reliable AC Repair in Phoenix
                        </h4>
                        <p className="text-gray-400 text-[11px] mb-4 leading-relaxed" data-id="element-3020" data-mp="23vfho0wpktrl">
                          Same-day service. Licensed & insured. Upfront pricing
                          with no surprises.
                        </p>
                        <div className="flex items-center gap-2" data-id="element-3021" data-mp="9kk74t1yr4m6">
                          <div className="flex-1 bg-viridia text-white text-center text-[11px] font-bold py-2 rounded-lg" data-id="element-3022" data-mp="4l79redguf5c">
                            Call Now
                          </div>
                          <div className="flex-1 bg-white/10 text-white text-center text-[11px] font-bold py-2 rounded-lg border border-white/20" data-id="element-3023" data-mp="29lrx3mpwnab9">
                            Get a Quote
                          </div>
                        </div>
                      </div>
                      {/* Trust strip */}
                      <div className="flex items-center justify-center gap-1 py-2 bg-gray-50 border-y border-gray-100" data-id="element-3024" data-mp="2nkhsmxx2t1xi">
                        {[0, 1, 2, 3, 4].map(s => <StarIcon key={s} className="w-3 h-3 text-yellow-400 fill-current" data-id="element-3025" data-mp="2xbuk3oeypq30" />)}
                        <span className="text-[10px] text-gray-500 ml-1" data-id="element-3026" data-mp="36ckb8eta2sae">
                          4.9 · 200+ reviews
                        </span>
                      </div>
                      {/* Services */}
                      <div className="px-4 py-4" data-id="element-3027" data-mp="3lfkeld101t8o">
                        <div className="text-gray-900 font-bold text-xs mb-3" data-id="element-3028" data-mp="3gemx72ntd388">
                          Our Services
                        </div>
                        <div className="grid grid-cols-2 gap-2" data-id="element-3029" data-mp="3bgd3yxlngcp9">
                          {['AC Repair', 'Installation', 'Maintenance', 'Tune-Ups'].map(svc => <div key={svc} className="bg-gray-50 border border-gray-100 rounded-lg p-2" data-id="element-3030" data-mp="32ctpwy0rjfo">
                              <div className="w-5 h-5 rounded bg-viridia/10 mb-1.5" data-id="element-3031" data-mp="3qd3vkkp30k4b"></div>
                              <div className="text-[10px] font-semibold text-gray-700" data-id="element-3032" data-mp="qrfc570h9gb8">
                                {svc}
                              </div>
                            </div>)}
                        </div>
                      </div>
                      {/* Sticky CTA bar */}
                      <div className="absolute bottom-0 left-0 right-0 bg-viridia px-4 py-3 flex items-center justify-between" data-id="element-3033" data-mp="1afpnfufigglw">
                        <span className="text-white text-[11px] font-bold" data-id="element-3034" data-mp="10njx0p4wq78t">
                          (623) 300-0708
                        </span>
                        <span className="bg-white text-viridia-dark text-[10px] font-bold px-3 py-1 rounded-full" data-id="element-3035" data-mp="1oefht2ay426k">
                          Tap to Call
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SEO & GEO SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-3036" data-mp="32fen9uzdt61">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3037" data-mp="80z3pj4vfl2m">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-3038" data-mp="2m6dsk02zkxhk">
            <ScrollReveal data-id="element-3039" data-mp="2h8c9hf5k8y63">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-3040" data-mp="2h70xwt5vim4f">
                Discoverability Support
              </h2>
              <p className="text-lg text-gray-400 mb-6 leading-relaxed" data-id="element-3041" data-mp="2v5r4czel1y5g">
                A great website is useless if no one can find it. We provide
                local SEO and GEO (Generative Engine Optimization) support to
                ensure your business is visible when it matters.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-3042" data-mp="1yfrmufggfpaq">
                <li className="flex items-start" data-id="element-3043" data-mp="2ca10t7r04tpg">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-3044" data-mp="2cau23t76yr07" />
                  <span className="text-gray-300" data-id="element-3045" data-mp="27g6avmkszihx">
                    Crawlable service content for search engines
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3046" data-mp="1fq1v617znjn1">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-3047" data-mp="1fpa4n4t90c3m" />
                  <span className="text-gray-300" data-id="element-3048" data-mp="9que30urf0yo">
                    Local relevance optimization (Google Business Profile
                    integration)
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3049" data-mp="jkl44a6jbjgh">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-3050" data-mp="eqyyv32pahup" />
                  <span className="text-gray-300" data-id="element-3051" data-mp="tg4vqke2fvzf">
                    Structured data (Schema markup) for AI discoverability
                  </span>
                </li>
              </ul>
            </ScrollReveal>

            <ScrollReveal direction="left" data-id="element-3052" data-mp="12gb7a0q7elvy">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 shadow-2xl" data-id="element-3053" data-mp="12f8dnl1hy5cc">
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-800" data-id="element-3054" data-mp="348zcugqbllew">
                  <div className="w-12 h-12 rounded-xl bg-viridia/10 flex items-center justify-center" data-id="element-3055" data-mp="34asjhw0xo5mw">
                    <CodeIcon className="w-6 h-6 text-viridia" data-id="element-3056" data-mp="1eru14uh22sfw" />
                  </div>
                  <div data-id="element-3057" data-mp="1eu6gr0zccqps">
                    <h3 className="text-xl font-bold text-white" data-id="element-3058" data-mp="1jqngksfwktip">
                      Technical Foundation
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-3059" data-mp="15yqrkx9j6zqj">
                      Built for modern search
                    </p>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-3060" data-mp="w0t44bzhx6yz">
                  <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-3061" data-mp="w018501zl786">
                    <div className="text-sm font-medium text-white mb-1" data-id="element-3062" data-mp="1120bwb1n265n">
                      Semantic HTML
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-3063" data-mp="r29he9d2vr5k">
                      Clear heading structures and accessible markup.
                    </div>
                  </div>
                  <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-3064" data-mp="2hcktmabik6n9">
                    <div className="text-sm font-medium text-white mb-1" data-id="element-3065" data-mp="2lfhfqb09ikvy">
                      Fast Hosting
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-3066" data-mp="3pee4af1n06wf">
                      Deployed on global edge networks for instant load times.
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-3067" data-mp="gx9k00dv1no9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3068" data-mp="cw7ifcwh0zj4">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3069" data-mp="cytzq495ytej">
            <ScrollReveal data-id="element-3070" data-mp="84prlho5wfbv">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-3071" data-mp="83wi48xtjbq1">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-3072" data-mp="10mbmtmhhh3b6">
                Our web design services are for local service businesses that
                need a strong digital foundation to support their ad campaigns
                and lead conversion efforts.
              </p>
              <ul className="space-y-4" data-id="element-3073" data-mp="1392tsmi4tfr2">
                <li className="flex items-start gap-3" data-id="element-3074" data-mp="1851dvxwfgblo">
                  <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-3075" data-mp="187nuso57k3gk" />
                  <div data-id="element-3076" data-mp="1d70hn8w2xaj5">
                    <h4 className="font-bold text-white" data-id="element-3077" data-mp="1h6j263jznjlg">Local Contractors</h4>
                    <p className="text-sm text-gray-400" data-id="element-3078" data-mp="1h4eo4ssdl47g">
                      HVAC, painters, landscapers, window installers, and other
                      businesses that rely on local search to find customers.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-3079" data-mp="1vw73h4s66i96">
                  <ZapIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-3080" data-mp="20v096b17t4ys" />
                  <div data-id="element-3081" data-mp="24wdcadcxau20">
                    <h4 className="font-bold text-white" data-id="element-3082" data-mp="24wv8xb3hzaag">
                      Businesses Running Ads
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-3083" data-mp="29vq296z3ch8t">
                      If you are paying for traffic, you need a website that
                      actually converts that traffic into leads.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-3084" data-mp="260dhc4am8lkl">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-3085" data-mp="20yo4cj3u1xtv">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-3086" data-mp="1d9bxvqzw43v9">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-3087" data-mp="198s2aruyt8b8">
                  <Link to="/ad-campaigns" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-3088" data-mp="19a34wococa0r">
                    <div className="font-bold text-white mb-1" data-id="element-3089" data-mp="2ymy8b3n6ivnm">
                      Ad Campaigns
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-3090" data-mp="2toda19rhmm1t">
                      Drive high-intent traffic to your new conversion website.
                    </div>
                  </Link>
                  <Link to="/ai-lead-conversion" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-3091" data-mp="kfow71qblf1f">
                    <div className="font-bold text-white mb-1" data-id="element-3092" data-mp="bfippzt1hfbr">
                      AI Lead Conversion
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-3093" data-mp="z668xh8enrdm">
                      Ensure every lead generated by your site gets an instant
                      response.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-3094" data-mp="1i2nzvwffjn8t">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-3095" data-mp="1mte3p587ji1e">
          <ScrollReveal data-id="element-3096" data-mp="1wpmlharlvnfm">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-3097" data-mp="1wq61un1ly3rj">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3098" data-mp="1mrgl4xlg6pcv">
          <ScrollReveal delay={0.2} data-id="element-3099" data-mp="1mpvql7z4v6f0">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-3100" data-mp="1hsdq85z6lzim">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-3101" data-mp="1drtrwz2vgpuw" data-mpi={index} />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-3102" data-mp="z24di25rqpco">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3103" data-mp="z01dcpawjfu8">
          <ScrollReveal data-id="element-3104" data-mp="u1zvg7p7pmkc">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-3105" data-mp="u4m6ihbbak7b">
              Ready for a site that converts?
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-3106" data-mp="31pb5j6sa6b6s">
              Book a strategy call to see how we can build a conversion-focused
              website for your business.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-viridia text-white hover:bg-viridia-dark transition-all duration-300" data-id="element-3107" data-mp="3aqj0bevl8tw4">
              Book a Strategy Call <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-3108" data-mp="2mzdm145aq9nx" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}